
import { Injectable, NgZone } from '@angular/core';
import { equalTo, get, getDatabase, off, onValue, orderByChild, query, ref, set, update, runTransaction } from 'firebase/database';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { PreferenceService } from './preference.service';
import { Motion } from '@capacitor/motion';
import { BgLocationPermission } from 'background-location-permission-plugin/src';
import { LocationQualityGate, distanceMeters } from '../helpers/location-quality.helper';

export interface UserLocation {
  userId: string;
  latitude: number;
  longitude: number;
  status?: string;
  timestamp: number;
  emegency: boolean;
  inside?: boolean;
  shiftData?: any;
  isLiveStream: any;
  beacon?: any;
  shiftId?: any;
  rosterId?: any;
}

interface CurrentLocation {
  lat: number;
  lng: number;
  time: number;
}

@Injectable({
  providedIn: 'root'
})

export class LocationService {
    private allUsersLocation$ = new BehaviorSubject<UserLocation[]>([]);
    private userLocations = new Map<string, BehaviorSubject<UserLocation | null>>();
    current$ = new BehaviorSubject<CurrentLocation | null>(null);
    isMoving$ = new BehaviorSubject<boolean>(false);
    private motionListener: any = null;
    private lastGood: CurrentLocation | null = null;
    private serviceStarted: boolean = false;

    // Shared by the background and foreground watchers so both streams use the same last-good baseline
    // and a jump rejected by one cannot be re-introduced by the other.
    readonly qualityGate = new LocationQualityGate();

    // ---- Config thresholds ----
    private MIN_DISTANCE = 10;           // min movement to accept (meters)
    private MAX_SPEED_JUMP = 40;         // reject unrealistically fast jump (m/s ≈ 144 km/h)
    private MAX_JUMP_DISTANCE = 100;     // reject sudden large distance if not moving (meters)
    private ACCEL_MOVING_THRESHOLD = 0.2;// "is moving" sensor threshold
    private lastMotionTime = 0;
    private MOTION_RECENT_MS = 3000;     // 3s window to consider "recent motion"

    constructor(private zone: NgZone, 
        private prefService: PreferenceService
    ){
        this.startSensorTracking();
    }

    // async init(){
    //     if(this.serviceStarted == false){
    //         this.serviceStarted = true;
    //         this.startSensorTracking();
    //     }
    // }

    async startSensorTracking() {
        await BgLocationPermission.addListener('motionState', async (event) => {
            console.log('Motion state changed:', event.state);
             // => "moving" or "still"
            const moving = event.state == 'moving';
            if (moving) {
                this.lastMotionTime = Date.now();
                await this.prefService.setLastActive();
            };
            this.isMoving$.next(moving);
        });
        // Watch accelerometer to detect movement
        // try {
        //     this.motionListener = await Motion.addListener('accel', (event: any) => {
        //     const a = event.acceleration ?? event.accelerationIncludingGravity ?? { x: 0, y: 0, z: 0 };
        //     const mag = Math.sqrt(a.x*a.x + a.y*a.y + a.z*a.z);

        //     // console.warn('Motion sensor: ', mag);
        //     // Threshold tuned for movement detection
        //     const moving = mag > 1.2;
        //     this.isMoving$.next(moving);

        //     if (moving) this.lastMotionTime = Date.now(); // remember when last movement occurred
        //     });
        // } catch (e) {
        //     console.warn('Motion sensor unavailable', e);
        // }
    }
    // status: On/ Off Duty
    // async pushUserLocation(tenantId: any, userId: any, lat: number, lng: number, status: string) {
    //     try {
    //         const db = getDatabase();
    //         const userLocationRef = ref(db, `locations/${tenantId}/${userId}`);
    //         // console.log("latlng:" + lat + " " + lng);
    //         await set(userLocationRef, {
    //             latitude: lat,
    //             longitude: lng,
    //             timestamp: Date.now(),
    //             status: status,
    //             emegency: false
    //         });
    //     } catch (ex) {
    //         console.error('sendMessage error:', ex);
    //     }
    // }
    async updateUserAllLiveStream(tenantId: any){
    try {
        if(await this.prefService.getUserLogged())
            {
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId}`);
                await update(userLocationRef, {
                    isLiveStream: false,
                    timestamp: Date.now(),
                });
            }
        
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    async updateUserLiveStream(
        tenantId: any,
        userId: any,
        isLiveStream: boolean
    ) 
    {
        try {
            if(await this.prefService.getUserLogged())
            {
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId}/${userId}`);
                await update(userLocationRef, {
                    isLiveStream: isLiveStream ?? false,
                    timestamp: Date.now(),
                });
            }
        
        } catch (ex) {
        console.error('sendMessage error:', ex);
        }
    }

    async setUserLocation(tenantId: any, userId: any, lat: number, lng: number, status: string) {
        try {
            if(await this.prefService.getUserLogged()){
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId}/${userId}`);
                // console.log("latlng:" + lat + " " + lng);
                await update(userLocationRef, {
                    latitude: lat,
                    longitude: lng,
                    timestamp: Date.now(),
                    status: status
                });
            }
            
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    // force=true bypasses filterAndSet()'s "no recent motion" gate — used by an explicit, user-initiated
    // push (e.g. the "Show My Location" button for staff on a roster with background tracking Off) where
    // the whole point is to report a position while stationary, unlike the passive/automatic callers below
    // which should keep respecting the motion filter.
    async updateUserLocation(tenantId: any, userId: any, lat: number, lng: number, rosterId: string, shiftId: string, force: boolean = false) {
        try {
            if(await this.prefService.getUserLogged()
            && (force || this.filterAndSet({lat: lat, lng: lng, time: Date.now()}))){
                // console.log("updateUserLocation: OK");
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId.toLowerCase()}/${userId.toLowerCase()}`);
                await update(userLocationRef, {
                    latitude: lat,
                    longitude: lng,
                    timestamp: Date.now(),
                    rosterId: rosterId,
                    shiftId: shiftId,
                });
            }

        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    // async updateUserStatus(tenantId: any, userId: any, status: string) {
    //     try {
    //         const db = getDatabase();
    //         const userLocationRef = ref(db, `locations/${tenantId}/${userId}`);
    //         await update(userLocationRef, {
    //             timestamp: Date.now(),
    //             status: status
    //         });
    //     } catch (ex) {
    //         console.error('sendMessage error:', ex);
    //     }
    // }

    async updateEmegencyStatus(tenantId: any, userId: any, emegency: boolean) {
        try {
            if(await this.prefService.getUserLogged()){
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId}/${userId}`);
                await update(userLocationRef, {
                    timestamp: Date.now(),
                    emegency: emegency
                });
            }
            
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    async getAllUserLocations(){
        try {
            const db = getDatabase();
            const userLocationRef = ref(db, `locations`);

            const q = query(userLocationRef, orderByChild('status'), equalTo('On'));
            get(q).then(snapshot => {
            if (snapshot.exists()) {
                const users = snapshot.val();
                console.log("Users on duty:", users);
            } else {
                console.log("No users found");
            }
            });
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

     listenToUsers(userIds: string[]) {
        const db = getDatabase();
        userIds.forEach(userId => {
        const userRef = ref(db, `locations/${userId}`);
        const sub = new BehaviorSubject<UserLocation | null>(null);
        this.userLocations.set(userId, sub);
        onValue(userRef, snapshot => {
            this.zone.run(() => {
                const data = snapshot.val();
                if (data) {
                    sub.next({ userId, ...data });
                } else {
                    sub.next(null);
                }
                });
            });
        });
    }

    private listening = false;
    listenToAllUsers(tenantId: any) {
        try{
             const db = getDatabase();
            const userLocationRef = ref(db, `locations/${tenantId}`);
            onValue(userLocationRef, snapshot => {
            this.zone.run(() => {
                const result: UserLocation[] = [];
                    snapshot.forEach(child => {
                        const data = child.val();
                        result.push({
                            userId: child.key!,
                            latitude: data.latitude,
                            longitude: data.longitude,
                            timestamp: data.timestamp,
                            status: data.status,
                            emegency: data.emegency,
                            isLiveStream: data.isLiveStream,
                            beacon: data.beacon,
                            shiftId: data.shiftId,
                            rosterId: data.rosterId
                        });
                    });

                    // console.log("result", result)
                
                    this.allUsersLocation$.next(result);
                });
            });
            this.listening = true;
        }catch(ex){
            console.log(ex);
        }
        
    }

    stopListening(tenantId: string, userId: string) {
        const db = getDatabase();
        const userRef = ref(db, `locations/${tenantId}/${userId}`);
        off(userRef);
        this.userLocations.delete(userId);
    }

    stopAll(tenantId: string) {
        this.userLocations.forEach((_, userId) => this.stopListening(tenantId, userId));
    }

    getUserLocationObservable(userId: string) {
        return this.userLocations.get(userId)?.asObservable();
    }
    
    getAllUserLocationsObservable() {
        return this.allUsersLocation$.asObservable();
    }

    stopListeningAll(tenantId: any) {
        const db = getDatabase();
        const userLocationsRef = ref(db, `locations/${tenantId}`);
        off(userLocationsRef);
        // Optionally clear data but DO NOT complete
        this.allUsersLocation$.next([]);
        this.listening = false;
    }

    async updateUserLocationByApi(tenantId: any, userId: any, lat: number, lng: number, rosterId: string, shifId: string) {
        try {
            console.log("updateUserLocationByApi");
            if(await this.prefService.getUserLogged() 
                && this.filterAndSet({lat: lat, lng: lng, time: Date.now()})){
                console.log("updateUserLocationByApi: OK");
                const url = `${environment.firebase.databaseURL}locations/${tenantId}/${userId}.json?auth=${environment.firebase.vapidKey}`;
                // console.log('Firebase response url:', url);
                const payload = {
                    latitude: lat,
                    longitude: lng,
                    timestamp: Date.now(),
                    rosterId: rosterId,
                    shiftId: shifId
                };
                const response = await CapacitorHttp.patch({
                    url,
                    params: {}, 
                    headers: { 'Content-Type': 'application/json' },
                    data: payload
                });
            }
            
            //console.log('Firebase response:', response);
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    private filterAndSet(fix: CurrentLocation): boolean {
        if(environment.production === false)
            return true;

        // console.log('filterAndSet', fix);
        const last = this.lastGood;
        if (!last) {
            // console.log('filterAndSet -> lastGood');
            this.accept(fix);
            return true;
        }

        const now = Date.now();
        
        let motionRecently = (now - this.lastMotionTime) < this.MOTION_RECENT_MS;
        //console.log('filterAndSet -> motionRecently:', motionRecently);
        //console.log('filterAndSet -> motionRecently:', (now - this.lastMotionTime));
        if(!motionRecently){
            if(Capacitor.getPlatform() === 'ios'){
                motionRecently = this.isMoving$.getValue();
            }
        }
        if (motionRecently) {
            console.log('motionRecently -> accept');
            this.accept(fix);
            return true;
        }

        return false;

        /*
        const last = this.lastGood;
        if (!last) {
            console.log('filterAndSet -> lastGood');
            this.accept(fix);
            return true;
        }

        const dist = this.distance({lat: last.lat, lng: last.lng},
             {lat: fix.lat, lng: fix.lng});
        const dt = Math.max(1, (fix.time - last.time) / 1000); // seconds
        const speed = dist / dt;

        // Check recent motion
        const now = Date.now();
        const motionRecently = (now - this.lastMotionTime) < this.MOTION_RECENT_MS;

        console.log("motionRecently:", motionRecently);
        console.log("speed:", speed);
        console.log("dist:", dist);

        // RULE: If not recently moved and distance is small -> ignore jitter
        if (!motionRecently || dist > this.MAX_JUMP_DISTANCE) {
            console.log('Ignored GPS change — no recent movement detected');
            return false;
        }

        // If movement confirmed and distance > MIN_DISTANCE -> accept
        if (motionRecently && dist < this.MAX_JUMP_DISTANCE) {
            console.log('Confirmed and distance > MIN_DISTANCE -> accept');
            this.accept(fix);
            return true;
        }

        return false;
        */
    }

    private accept(loc: CurrentLocation) {
        this.lastGood = loc;
        this.current$.next(loc);
        console.log('✅ GPS update accepted:', loc);
    }

    private distance(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
        return distanceMeters(a, b);
   }

    async updateLocationByBeacon(tenantId: any, userId: any, 
        name: string, mac: string, floor: number, unit: number, status: string, lng: number, 
        lat: number, rssi: number, floorCode: string, roomCode: string, distance: number, detectMotion: boolean) {
        try {
            const now = Date.now();
            const motionRecently = (now - this.lastMotionTime) < this.MOTION_RECENT_MS;
            console.log("motionRecently:", motionRecently);
            if (!motionRecently && detectMotion) {
                return;
            }
            const isUserLogged = await this.prefService.getUserLogged();
            console.log("isUserLogged:", isUserLogged);

            if(isUserLogged){
                // console.log("updateUserLocation: OK");
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId.toLowerCase()}/${userId.toLowerCase()}`);
                await update(userLocationRef, {
                    timestamp: Date.now(),
                    "beacon/name": name,
                    "beacon/mac": mac,
                    "beacon/floor": floor,
                    "beacon/unit": unit,
                    "beacon/status": status,
                    "beacon/lng": lng,
                    "beacon/lat": lat,
                    "beacon/rssi": rssi,
                    "beacon/floorCode": floorCode,
                    "beacon/roomCode": roomCode,
                    "beacon/distance": distance
                });
            }
            
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }

    async updateLocationByTag(tenantId: any, userId: any, 
        name: string, mac: string, floor: number, unit: number, status: string, lng: number, 
        lat: number, rssi: number, floorCode: string, roomCode: string, distance: number) {
        try {
            const now = Date.now();
            if(await this.prefService.getUserLogged()){
                // console.log("updateUserLocation: OK");
                const db = getDatabase();
                const userLocationRef = ref(db, `locations/${tenantId.toLowerCase()}/${userId.toLowerCase()}`);
                await update(userLocationRef, {
                    timestamp: Date.now(),
                    beacon: {
                        name: name,
                        mac: mac,
                        floor: floor,
                        unit: unit,
                        status: status,
                        lng: lng,
                        lat: lat,
                        rssi: rssi,
                        floorCode: floorCode,
                        roomCode: roomCode,
                        distance: distance
                    }
                });
            }
            
        } catch (ex) {
            console.error('sendMessage error:', ex);
        }
    }
}